import type { AxiosResponse } from 'axios'
import type {
  AccessUsersResponse,
  AddAccessPayload,
  AggregatedChartData,
  BradleyCurveResponse,
  CommentAnalysisByAreaResponse,
  CommentAnalysisByDemographicRow,
  CommentAnalysisByTheme,
  CommentAnalysisCommentType,
  CommentAnalysisData,
  CommentAnalysisSummary,
  CommentSentiment,
  CommentThemePage,
  ContrastByDemographicData,
  ContrastData,
  ContrastDemographicOption,
  CreateProjectPayload,
  DatabricksSyncExecution,
  DemographicCategory,
  DemographicType,
  ExportJob,
  ExportJobStatus,
  FilterOption,
  FrameworkMatrixRow,
  FrameworkSummaryResponse,
  GlobalFilter,
  Industry,
  LanguagesTemplateJob,
  LanguagesTemplateJobStatus,
  MaturityJourneyItem,
  PaginatedCommentAnalysisSentiment,
  PaginatedResponse,
  ProgressReportPdfJob,
  ProgressReportPdfJobStatus,
  Project,
  ProjectFilters,
  ProjectSummary,
  QuestionBreakdownByDemographicData,
  QuestionBreakdownData,
  QuestionBreakdownGroupBy,
  QuestionOption,
  UpdateAccessPayload,
  UserProjectAccessScope,
  ViewingPermissionsResponse,
} from '../types'
import { apiClient } from '@/shared/services/http/http.service'

const projectsEndpoint = 'projects'
const industriesEndpoint = 'industries'

export const SpsService = {
  getSurveys: ({
    filters = {},
    page = 1,
    pageSize = 6,
  }: {
    filters?: ProjectFilters
    page?: number
    pageSize?: number
  }): Promise<AxiosResponse<PaginatedResponse<ProjectSummary>, unknown>> => {
    const parameters = new URLSearchParams()
    parameters.append('paginate', 'true')
    if (filters.status && filters.status !== 'all') {
      parameters.append('status', filters.status)
    }
    if (filters.name) {
      parameters.append('name', filters.name)
    }
    parameters.append('page', page.toString())
    parameters.append('limit', pageSize.toString())
    return apiClient('sps').get<PaginatedResponse<ProjectSummary>>(`${projectsEndpoint}`, { params: parameters })
  },

  createProject: (payload: CreateProjectPayload): Promise<AxiosResponse<ProjectSummary, unknown>> => {
    return apiClient('sps').post<ProjectSummary>(`${projectsEndpoint}`, payload)
  },

  getSurveyDetails: (id: string): Promise<AxiosResponse<Project, unknown>> => {
    return apiClient('sps').get<Project>(`${projectsEndpoint}/${id}`)
  },

  activateProject: (id: string, content?: object): Promise<AxiosResponse<Project, unknown>> => {
    return apiClient('sps').post<Project>(`${projectsEndpoint}/${id}/publish`, content)
  },

  updateProject: (id: string, payload: Partial<CreateProjectPayload>): Promise<AxiosResponse<Project, unknown>> => {
    return apiClient('sps').put<Project>(`${projectsEndpoint}/${id}`, payload)
  },

  deleteProject: (id: string): Promise<AxiosResponse<void, unknown>> => {
    return apiClient('sps').delete<void>(`${projectsEndpoint}/${id}`)
  },

  closeProject: (id: string): Promise<AxiosResponse<Project, unknown>> => {
    return apiClient('sps').put<Project>(`${projectsEndpoint}/${id}`, { status: 'closed' })
  },

  // Reverses a close: the dedicated endpoint clears both gates that stop a
  // project from taking responses (its `closed` status and the end date
  // mirrored onto the survey), which a plain status update does not do.
  // An empty body reopens indefinitely.
  reopenProject: (id: string): Promise<AxiosResponse<Project, unknown>> => {
    return apiClient('sps').post<Project>(`${projectsEndpoint}/${id}/reopen`, {})
  },

  getDatabricksSyncStatus: (id: string, lang?: string): Promise<AxiosResponse<DatabricksSyncExecution | null>> => {
    const parameters = new URLSearchParams()
    if (lang)
      parameters.append('lang', lang)
    return apiClient('sps').get<DatabricksSyncExecution | null>(`${projectsEndpoint}/${id}/databricks-sync`, { params: parameters })
  },

  startDatabricksSync: (id: string, lang?: string): Promise<AxiosResponse<DatabricksSyncExecution>> => {
    const parameters = new URLSearchParams()
    if (lang)
      parameters.append('lang', lang)
    return apiClient('sps').post<DatabricksSyncExecution>(`${projectsEndpoint}/${id}/databricks-job`, undefined, { params: parameters })
  },

  getProjectAccess: (id: string): Promise<AxiosResponse<ViewingPermissionsResponse, unknown>> => {
    return apiClient('sps').get<ViewingPermissionsResponse>(`${projectsEndpoint}/${id}/viewing-permissions`)
  },

  getAccessConditionFields: (id: string, language?: string): Promise<AxiosResponse<FilterOption[], unknown>> => {
    const parameters = new URLSearchParams()
    if (language)
      parameters.append('language', language)
    return apiClient('sps').get<FilterOption[]>(`${projectsEndpoint}/${id}/cultural-strength/filters`, { params: parameters })
  },

  searchAccessUsers: (query?: string, limit: number = 50): Promise<AxiosResponse<AccessUsersResponse, unknown>> => {
    const parameters = new URLSearchParams()
    if (query)
      parameters.append('q', query)
    parameters.append('limit', String(limit))
    return apiClient('sps').get<AccessUsersResponse>(`${projectsEndpoint}/viewing-permissions/users`, { params: parameters })
  },

  getUserProjectAccessScope: (): Promise<AxiosResponse<UserProjectAccessScope, unknown>> => {
    return apiClient('sps').get<UserProjectAccessScope>(`${projectsEndpoint}/viewing-permissions/access-scope`)
  },

  addProjectAccess: (id: string, payload: AddAccessPayload): Promise<AxiosResponse<unknown, unknown>> => {
    return apiClient('sps').post(`${projectsEndpoint}/${id}/viewing-permissions`, payload)
  },

  updateProjectAccess: (id: string, payload: UpdateAccessPayload): Promise<AxiosResponse<unknown, unknown>> => {
    return apiClient('sps').put(`${projectsEndpoint}/${id}/viewing-permissions`, payload)
  },

  removeProjectAccess: (id: string, userId: string): Promise<AxiosResponse<void, unknown>> => {
    const parameters = new URLSearchParams()
    parameters.append('userId', userId)
    return apiClient('sps').delete<void>(`${projectsEndpoint}/${id}/viewing-permissions`, { params: parameters })
  },

  getSurveyJSON: (id: string): Promise<AxiosResponse<object>> => {
    return apiClient('sps').get<object>(`${projectsEndpoint}/${id}/survey`)
  },

  updateSurveyJSON: (id: string, payload: object): Promise<AxiosResponse> => {
    return apiClient('sps').put(`${projectsEndpoint}/${id}/survey`, payload)
  },

  getIndustries: (): Promise<AxiosResponse<Industry[] | undefined>> => {
    return apiClient('sps').get<Industry[]>(`${industriesEndpoint}`)
  },

  getIndustryById: (id: string): Promise<AxiosResponse<Industry | undefined>> => {
    return apiClient('sps').get<Industry>(`${industriesEndpoint}/${id}`)
  },

  getLanguagesTemplate: (id: string, languages: string[]): Promise<AxiosResponse<Blob>> => {
    return apiClient('sps').get<Blob>(`${projectsEndpoint}/${id}/languages-template`, {
      params: { languages: languages.join(',') },
      responseType: 'blob',
    })
  },

  enqueueLanguagesTemplate: async (id: string, languages: string[]): Promise<AxiosResponse<LanguagesTemplateJob>> => {
    const response = await apiClient('sps').post<LanguagesTemplateJob>(
      `${projectsEndpoint}/${id}/languages-template/jobs`,
      undefined,
      { params: { languages: languages.join(',') } },
    )

    if (response.status !== 202) {
      throw new Error(response.statusText)
    }
    return response
  },

  getLanguagesTemplateJobStatus: (id: string, jobId: string): Promise<AxiosResponse<LanguagesTemplateJobStatus>> => {
    return apiClient('sps').get<LanguagesTemplateJobStatus>(`${projectsEndpoint}/${id}/languages-template/jobs/${jobId}`)
  },

  downloadLanguagesTemplateJob: (id: string, jobId: string): Promise<AxiosResponse<Blob>> => {
    return apiClient('sps').get<Blob>(`${projectsEndpoint}/${id}/languages-template/jobs/${jobId}/download`, {
      responseType: 'blob',
    })
  },

  cancelLanguagesTemplateJob: (id: string, jobId: string): Promise<AxiosResponse<LanguagesTemplateJobStatus>> => {
    return apiClient('sps').delete<LanguagesTemplateJobStatus>(`${projectsEndpoint}/${id}/languages-template/jobs/${jobId}`)
  },

  postLanguagesTemplate: (id: string, file: Blob, publish: boolean = false): Promise<AxiosResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient('sps').post(`${projectsEndpoint}/${id}/languages-template?publish=${publish}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  getDemographicTypes: (): Promise<AxiosResponse<DemographicType[] | undefined>> => {
    return apiClient('sps').get<DemographicType[]>(`${projectsEndpoint}/demographic-types`)
  },

  getDemographicTypesByProject: (id: string): Promise<AxiosResponse<DemographicType[] | undefined>> => {
    return apiClient('sps').get<DemographicType[]>(`${projectsEndpoint}/${id}/demographics`)
  },

  getAggregatedChartByCategory: (id: string, category: string): Promise<AxiosResponse<AggregatedChartData | undefined>> => {
    return apiClient('sps').get<AggregatedChartData>(`${projectsEndpoint}/${id}/response-details/${category}`)
  },

  enqueueProgressReportPdf: async (id: string, demographics: string[]): Promise<AxiosResponse<ProgressReportPdfJob>> => {
    const response = await apiClient('sps').post<ProgressReportPdfJob>(
      `${projectsEndpoint}/${id}/progress-report/jobs`,
      undefined,
      { params: { demographics: demographics.join(',') } },
    )

    if (response.status !== 202) {
      throw new Error(response.statusText)
    }
    return response
  },

  getProgressReportPdfJobStatus: (id: string, jobId: string): Promise<AxiosResponse<ProgressReportPdfJobStatus>> => {
    return apiClient('sps').get<ProgressReportPdfJobStatus>(`${projectsEndpoint}/${id}/progress-report/jobs/${jobId}`)
  },

  downloadProgressReportPdfJob: (id: string, jobId: string): Promise<AxiosResponse<Blob>> => {
    return apiClient('sps').get<Blob>(`${projectsEndpoint}/${id}/progress-report/jobs/${jobId}/download`, {
      responseType: 'blob',
    })
  },

  cancelProgressReportPdfJob: (id: string, jobId: string): Promise<AxiosResponse<ProgressReportPdfJobStatus>> => {
    return apiClient('sps').delete<ProgressReportPdfJobStatus>(`${projectsEndpoint}/${id}/progress-report/jobs/${jobId}`)
  },

  exportProjectPpt: async (id: string, globalFilters: GlobalFilter[], lang: string): Promise<AxiosResponse<Blob>> => {
    const response = await apiClient('sps').post<Blob>(
      `${projectsEndpoint}/${id}/progress-report-pptx?language=${lang}`,
      { globalFilters },
      { responseType: 'blob' },
    )

    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return response
  },

  exportSyncBatchProjectPpt: async (id: string, globalFilters: GlobalFilter[], splitCriteria: string, lang: string): Promise<AxiosResponse<Blob>> => {
    const response = await apiClient('sps').post<Blob>(
      `${projectsEndpoint}/${id}/progress-report-pptx/multiple?language=${lang}`,
      {
        splitCriteria,
        globalFilters,
      },
      { responseType: 'blob' },
    )

    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return response
  },

  enqueueProjectPptMultiple: async (id: string, globalFilters: GlobalFilter[], splitCriteria: string, lang: string): Promise<AxiosResponse<ExportJob>> => {
    const response = await apiClient('sps').post<ExportJob>(
      `${projectsEndpoint}/${id}/progress-report-pptx/multiple/jobs?language=${lang}`,
      {
        splitCriteria,
        globalFilters,
      },
    )

    if (response.status !== 202) {
      throw new Error(response.statusText)
    }
    return response
  },

  enqueueProjectPptSingle: async (id: string, globalFilters: GlobalFilter[], lang: string): Promise<AxiosResponse<ExportJob>> => {
    const response = await apiClient('sps').post<ExportJob>(
      `${projectsEndpoint}/${id}/progress-report-pptx/jobs?language=${lang}`,
      {
        globalFilters,
      },
    )

    if (response.status !== 202) {
      throw new Error(response.statusText)
    }
    return response
  },

  getExportJobStatus: async (projectId: string, jobId: string): Promise<AxiosResponse<ExportJobStatus>> => {
    return apiClient('sps').get<ExportJobStatus>(`${projectsEndpoint}/${projectId}/progress-report-pptx/jobs/${jobId}`)
  },

  downloadProjectPpt: async (projectId: string, jobId: string): Promise<AxiosResponse<Blob>> => {
    return apiClient('sps').get<Blob>(`${projectsEndpoint}/${projectId}/progress-report-pptx/jobs/${jobId}/download`, { responseType: 'blob' },
    )
  },

  getContrastDemographics: (
    id: string,
    lang: string,
  ): Promise<AxiosResponse<ContrastDemographicOption[], unknown>> => {
    const parameters = new URLSearchParams()
    if (lang)
      parameters.append('language', lang)
    return apiClient('sps').get<ContrastDemographicOption[]>(
      `${projectsEndpoint}/${id}/cultural-strength/contrast/demographics`,
      { params: parameters },
    )
  },

  getContrast: (
    id: string,
    options: {
      demographic: string
      lang: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<ContrastData, unknown>> => {
    const parameters = new URLSearchParams()
    parameters.append('demographic', options.demographic)
    if (options.lang)
      parameters.append('language', options.lang)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, values.join(','))
      })
    }
    return apiClient('sps').get<ContrastData>(
      `${projectsEndpoint}/${id}/cultural-strength/contrast`,
      { params: parameters },
    )
  },

  getContrastOverallSpreadByDemographic: (
    projectId: string,
    options: {
      demographic: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<ContrastByDemographicData>> => {
    const parameters = new URLSearchParams()
    parameters.append('demographic', options.demographic)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, values.join(','))
      })
    }
    return apiClient('sps').get<ContrastByDemographicData>(
      `${projectsEndpoint}/${projectId}/insights/contrast/consolidated-view/overall-spread-by-demographic`,
      { params: parameters },
    )
  },

  getQuestionBreakdownQuestions: (id: string, lang: string): Promise<AxiosResponse<QuestionOption[], unknown>> => {
    const parameters = new URLSearchParams()
    if (lang)
      parameters.append('language', lang)
    return apiClient('sps').get<QuestionOption[]>(
      `${projectsEndpoint}/${id}/cultural-strength/question-breakdown/questions`,
      { params: parameters },
    )
  },

  getQuestionBreakdown: (
    id: string,
    options: {
      questionId: string
      lang: string
      groupBy: QuestionBreakdownGroupBy
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<QuestionBreakdownData, unknown>> => {
    const parameters = new URLSearchParams()
    parameters.append('questionId', options.questionId)
    parameters.append('groupBy', options.groupBy)
    if (options.lang)
      parameters.append('language', options.lang)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, values.join(','))
      })
    }
    return apiClient('sps').get<QuestionBreakdownData>(
      `${projectsEndpoint}/${id}/cultural-strength/question-breakdown`,
      { params: parameters },
    )
  },

  getQuestionBreakdownByDemographic: (
    projectId: string,
    options: {
      demographic: string
      question: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<QuestionBreakdownByDemographicData>> => {
    const parameters = new URLSearchParams()
    parameters.append('demographic', options.demographic)
    parameters.append('question', options.question)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<QuestionBreakdownByDemographicData>(
      `${projectsEndpoint}/${projectId}/insights/question-breakdown/by-demographic`,
      { params: parameters },
    )
  },

  getBradleyCurveByDemographic: (
    projectId: string,
    demographic: string,
    globalFilters: Record<string, string[]> = {},
    language?: string,
  ): Promise<AxiosResponse<BradleyCurveResponse>> => {
    const parameters = new URLSearchParams()
    if (language)
      parameters.append('language', language)
    parameters.append('demographic', demographic)
    Object.entries(globalFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0)
        parameters.append(key, [...values].join(','))
    })
    return apiClient('sps').get<BradleyCurveResponse>(
      `${projectsEndpoint}/${projectId}/insights/bradley-curve`,
      { params: parameters },
    )
  },

  getMaturityJourney: (
    projectId: string,
    demographicFilter: string,
    globalFilters: Record<string, string[]> = {},
    language?: string,
  ): Promise<AxiosResponse<MaturityJourneyItem[]>> => {
    const parameters = new URLSearchParams()
    if (language)
      parameters.append('language', language)
    const flatFilters = Object.entries(globalFilters).flatMap(
      ([filter, values]) => (Array.isArray(values) ? values : []).map(value => ({ filter, value })),
    )
    return apiClient('sps').post<MaturityJourneyItem[]>(
      `${projectsEndpoint}/${projectId}/maturity-journey`,
      { demographicFilter, globalFilters: flatFilters },
      { params: parameters },
    )
  },

  getFrameworkSummary: (
    projectId: string,
    globalFilters: Record<string, string[]> = {},
    year?: number,
    language?: string,
  ): Promise<AxiosResponse<FrameworkSummaryResponse>> => {
    const parameters = new URLSearchParams()
    if (language)
      parameters.append('language', language)
    if (year !== undefined && year !== null)
      parameters.append('year', String(year))
    Object.entries(globalFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0)
        parameters.append(key, [...values].join(','))
    })
    return apiClient('sps').get<FrameworkSummaryResponse>(
      `${projectsEndpoint}/${projectId}/insights/framework-summary`,
      { params: parameters },
    )
  },

  getFrameworkMatrix: (
    projectId: string,
    filter: DemographicCategory,
    language?: string,
  ): Promise<AxiosResponse<FrameworkMatrixRow[]>> => {
    const parameters = new URLSearchParams()
    if (language)
      parameters.append('language', language)
    return apiClient('sps').post<FrameworkMatrixRow[]>(
      `${projectsEndpoint}/${projectId}/framework-matrix`,
      { filter },
      { params: parameters },
    )
  },

  getCommentAnalysis: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<CommentAnalysisData>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentAnalysisData>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis`,
      { params: parameters },
    )
  },

  getCommentAnalysisSummary: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<CommentAnalysisSummary>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentAnalysisSummary>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis/summary`,
      { params: parameters },
    )
  },

  getCommentAnalysisBySentiment: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<AxiosResponse<PaginatedCommentAnalysisSentiment>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.sentiment)
      parameters.append('sentiment', options.sentiment)
    if (options.language)
      parameters.append('language', options.language)
    if (options.page)
      parameters.append('page', options.page.toString())
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<PaginatedCommentAnalysisSentiment>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis/by-sentiment`,
      { params: parameters },
    )
  },

  getCommentAnalysisByDemographic: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      demographic: string
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<CommentAnalysisByDemographicRow[]>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    parameters.append('demographic', options.demographic)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentAnalysisByDemographicRow[]>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis/by-demographic`,
      { params: parameters },
    )
  },

  getCommentAnalysisByTheme: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<AxiosResponse<CommentAnalysisByTheme>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.sentiment)
      parameters.append('sentiment', options.sentiment)
    if (options.language)
      parameters.append('language', options.language)
    if (options.page)
      parameters.append('page', options.page.toString())
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentAnalysisByTheme>(
      `${projectsEndpoint}/${projectId}/insights/comment-themes`,
      { params: parameters },
    )
  },

  getCommentAnalysisThemePage: (
    projectId: string,
    themeId: string,
    options: {
      commentType: CommentAnalysisCommentType
      sentiment?: CommentSentiment
      language?: string
      filters?: Record<string, string[]>
      page?: number
    },
  ): Promise<AxiosResponse<CommentThemePage>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.sentiment)
      parameters.append('sentiment', options.sentiment)
    if (options.language)
      parameters.append('language', options.language)
    if (options.page)
      parameters.append('page', options.page.toString())
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentThemePage>(
      `${projectsEndpoint}/${projectId}/insights/comment-themes/${themeId}`,
      { params: parameters },
    )
  },

  getCommentAnalysisByArea: (
    projectId: string,
    options: {
      commentType: CommentAnalysisCommentType
      demographic: string
      language?: string
      page?: number
      pageSize?: number
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<CommentAnalysisByAreaResponse>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    parameters.append('demographic', options.demographic)
    if (options.language)
      parameters.append('language', options.language)
    if (options.page !== undefined)
      parameters.append('page', String(options.page))
    if (options.pageSize !== undefined)
      parameters.append('pageSize', String(options.pageSize))
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<CommentAnalysisByAreaResponse>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis/by-area`,
      { params: parameters },
    )
  },

  exportCommentAnalysisBySentiment: (
    projectId: string,
    sentiment: CommentSentiment,
    options: {
      commentType: CommentAnalysisCommentType
      language?: string
      filters?: Record<string, string[]>
    },
  ): Promise<AxiosResponse<Blob>> => {
    const parameters = new URLSearchParams()
    parameters.append('commentType', options.commentType)
    if (options.language)
      parameters.append('language', options.language)
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0)
          parameters.append(key, [...values].join(','))
      })
    }
    return apiClient('sps').get<Blob>(
      `${projectsEndpoint}/${projectId}/insights/comment-analysis/${sentiment}/export`,
      { params: parameters, responseType: 'blob' },
    )
  },

  getCulturalStrengthFilters: async (id: string, lang: string): Promise<AxiosResponse<any, unknown>> => {
    // return apiClient('sps').get<any>(`${projectsEndpoint}/${id}/cultural-strength/filters?language=${lang}`)
    // remove this later
    if (!lang)
      console.error('locale not found')

    return apiClient('sps').get<any>(`${projectsEndpoint}/${id}/cultural-strength/filters`)
  },

  getCulturalStrengthFilterOptions: async (id: string, lang: string, demographic: string, dynamicFilters: Record<string, string[]>): Promise<AxiosResponse<any, unknown>> => {
    if (!lang)
      console.error('locale not found')

    let url = `${projectsEndpoint}/${id}/cultural-strength/filters/${demographic}`
    const query = buildQueryString(dynamicFilters)
    if (query) {
      url = url.concat(`?${query}`)
    }
    return apiClient('sps').get<any>(url)
  },

  getCompanyOverall: async (projectId: string, industries: string[], lang: string): Promise<AxiosResponse<any, unknown>> => {
    // remove this later
    if (!lang)
      console.error('locale not found')

    return apiClient('sps').post<any>(
      `${projectsEndpoint}/${projectId}/overall-info`,
      { industries },
    )
  },
}

function buildQueryString(filters: Record<string, string[]>): string {
  return Object.entries(filters)
    .filter(([_, values]) => values && values.length > 0)
    .map(([key, values]) => {
      const stringifiedValues = values.join(',')

      return `${encodeURIComponent(key)}=${encodeURIComponent(stringifiedValues)}`
    })
    .join('&')
}
